import React, { Component } from 'react';
import PropTypes from 'prop-types';
import utils from '../../../utils';
import styles from './index.module.css';

/**
 * Slider settings; below are some that may seem ambiguous:
 * @property {number} step - slider value step
 * @property {number} width - container width in % of its parent
 * @property {string} color - slider progress bar background color
 * @property {string} backgroundColor - container background color
 */

const DEFAULT_SETTINGS = {
  disabled: false,
  initialValue: 0,
  step: 1,
  precision: 1,
  width: 100,
  color: '#0f0',
  backgroundColor: '#fff'
};

class Slider extends Component {

  static getPositionPercentage (val, max, min = 0) {
    return Math.min(100, Math.max(0, 100 * (val - min) / (max - min)));
  }

  static getValueFromPercentage (percentage, max, min) {
    return min + (max - min) * percentage * 0.01;
  }

  /**
   * Get cursor position relative to the left/top borders of the slider's container
   * @param cursorClientPos - clientX & clientY of the cursor
   * @param container
   * @returns {{x: number, y: number}}
   */
  static getRelativeCursorPosition (cursorClientPos, container) {
    const containerBounds = container.getBoundingClientRect();

    return {
      x: cursorClientPos.x - containerBounds.left,
      y: cursorClientPos.y - containerBounds.top
    };
  }

  /**
   * Get handle position relative to the left/top borders of its container
   * @param handleElement
   * @param container
   * @returns {{x: number, y: number}}
   */
  static getRelativeHandlePosition (handleElement, container) {
    const bounds = handleElement.getBoundingClientRect();

    return Slider.getRelativeCursorPosition({
      x: (bounds.left + bounds.right) * 0.5,
      y: (bounds.top + bounds.bottom) * 0.5
    }, container);
  }

  constructor (props) {
    super(props);

    const tickVals = this.props.settings.vals.map(val => val.value);
    const tickLabels = this.props.settings.vals.map(val => val.label);

    this.settings = Object.assign({}, DEFAULT_SETTINGS, {
      initialValue: props.model,
      min: tickVals[0],
      max: tickVals[tickVals.length - 1],
    }, this.props.settings);

    this.dragData = {
      startValue: null,
      startHandlePos: null,
      startMousePos: null,
      containerWidth: null
    };

    this.ticks = tickVals;
    this.notches = tickLabels;

    this.onClick = this.onClick.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);

    this.state = {
      value: this.settings.initialValue
    };
  }

  formatValue (val) {
    return +(val.toFixed(this.settings.precision));
  }

  /**
   * Get value from the relative cursor position
   * @param cursorPos - cursor position relative to the container's left bound
   * @param containerWidth
   */
  getValueFromPosition (cursorPos, containerWidth) {
    const {min, max} = this.settings;
    const percent = Slider.getPositionPercentage(cursorPos, containerWidth);

    return Slider.getValueFromPercentage(percent, max, min);
  }

  adjustValue (val) {
    const ticksLen = this.ticks.length;
    const vals = this.ticks;

    if (val <= vals[0]) return vals[0];
    if (val >= vals[ticksLen - 1]) return vals[ticksLen - 1];

    for (let i = 0; i < ticksLen - 1; i++) {
      if (val === vals[i] || Math.abs(vals[i] - val) <= (vals[i + 1] - vals[i]) * 0.5) {
        return vals[i];
      }
    }

    return vals[ticksLen - 1];
  }

  onClick (event) {
    if (this.settings.disabled) {
      return;
    }

    const curPos = utils.getCursorPosition(event);
    const containerWidth = this.containerElement.getBoundingClientRect().width;

    this.setValue(
      this.getValueFromPosition(
        Slider.getRelativeCursorPosition(
          {x: curPos.cx, y: curPos.cy},
          this.containerElement
        ).x,
        containerWidth
      )
    );
  }

  onMouseDown (curValue, event) {
    if (this.settings.disabled) {
      return;
    }

    const curPos = utils.getCursorPosition(event);

    this.dragData.startValue = curValue;
    this.dragData.containerWidth = this.containerElement.getBoundingClientRect().width;
    this.dragData.startHandlePos = Slider.getRelativeHandlePosition(
      this.handleElement,
      this.containerElement
    );
    this.dragData.startMousePos = {
      x: curPos.px,
      y: curPos.py
    };
    event.currentTarget.focus();

    document.addEventListener(utils.events.mousemove, this.onMouseMove);
    document.addEventListener(utils.events.mouseup, this.onMouseUp);

    event.preventDefault();
  }

  onMouseMove (event) {
    const curPos = utils.getCursorPosition(event);
    const value = this.getValueFromPosition(
      this.dragData.startHandlePos.x + curPos.px - this.dragData.startMousePos.x,
      this.dragData.containerWidth
    );

    this.setValue(value);

    event.preventDefault();
  }

  onMouseUp (event) {
    document.removeEventListener(utils.events.mousemove, this.onMouseMove);
    document.removeEventListener(utils.events.mouseup, this.onMouseUp);

    for (let key in this.dragData) {
      if (this.dragData.hasOwnProperty(key)) {
        this.dragData[key] = null;
      }
    }

    event.preventDefault();
  }

  onKeyDown (value, event) {
    if (this.settings.disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
      case 'PageDown':
        this.setPrevValue();
        event.preventDefault();
        break;
      case 'Home':
        this.setValue(this.settings.min);
        event.preventDefault();
        break;
      case 'ArrowRight':
      case 'ArrowUp':
      case 'PageUp':
        this.setNextValue();
        event.preventDefault();
        break;
      case 'End':
        this.setValue(this.settings.max);
        event.preventDefault();
        break;
      default:
        break;
    }
  }

  setInternalValue (value, callback) {
    const fvalue = this.formatValue(this.adjustValue(value));
    if (this.state.value === fvalue) return;
    this.setState({value: fvalue}, callback);
  }

  propagateState () {
    this.props.onChange(this.state.value);
  }

  setValue (value) {
    this.setInternalValue(value, () => {
      this.propagateState();
    });
  }

  setNextValue () {
    const val = this.state.value;
    const ticksLen = this.ticks.length;
    const vals = this.ticks;

    if (val <= vals[0]) return vals[0];
    if (val >= vals[ticksLen - 1]) return vals[ticksLen - 1];

    for (let i = 0; i < ticksLen - 1; i++) {
      if (val >= vals[i] && val < vals[i + 1]) {
        return vals[i + 1];
      }
    }

    return vals[ticksLen - 1];
  }

  setPrevValue () {
    const val = this.state.value;
    const ticksLen = this.ticks.length;
    const vals = this.ticks;

    if (val <= vals[0]) return vals[0];
    if (val >= vals[ticksLen - 1]) return vals[ticksLen - 1];

    for (let i = 0; i < ticksLen - 1; i++) {
      if (val > vals[i] && val <= vals[i + 1]) {
        return vals[i];
      }
    }

    return vals[0];
  }

  componentWillReceiveProps (props) {
    if (this.state.value !== props.model) {
      this.setInternalValue(props.model);
    }
    if ('disabled' in props && props.disabled !== this.settings.disabled) {
      this.settings.disabled = props.disabled;
    }
  }

  render () {
    const props = this.props;
    const value = this.state.value;
    const settings = this.settings;
    const percentage = Slider.getPositionPercentage(value, settings.max, settings.min);

    const outerStyle = Object.assign({ width: `${settings.width}%` }, settings.outerStyle);
    const containerStyle = {
      opacity: settings.disabled ? 0.35 : 1
    };
    const progressStyle = { backgroundColor: settings.color };
    const handleStyle = { left: `${percentage}%` };
    const disabled = (typeof props.disabled === 'undefined')
      ? (settings.disabled || null) : props.disabled;

    return <div style={outerStyle}>
      <div className={styles.slider_container} style={containerStyle}>
        <div className={styles.slider_bg} ref={element => { this.containerElement = element }}>
          <ul className={styles.slider_tick_container} aria-hidden='true'>
            {this.notches.map((tick, idx) =>
              <li
                key={tick}
                aria-hidden='true'
                onClick={this.onClick}
                className={styles.slider_tick}
                style={
                  {left: `${Slider.getPositionPercentage(this.ticks[idx], settings.max, settings.min)}%`}
                }>{tick}</li>
            )}
          </ul>
          <div className={styles.slider_progress} style={progressStyle} onClick={this.onClick}/>
          <div
            {...props.attrs}
            role='slider'
            style={handleStyle}
            aria-valuenow={value}
            aria-hidden={disabled}
            aria-disabled={disabled}
            aria-valuemin={settings.min}
            aria-valuemax={settings.max}
            tabIndex={disabled ? -1 : 0}
            aria-orientation='horizontal'
            className={styles.slider_handle}
            ref={element => { this.handleElement = element }}
            onKeyDown={event => this.onKeyDown(value, event)}
            onMouseDown={event => this.onMouseDown(value, event)}
            onTouchStart={event => this.onMouseDown(value, event)}
            aria-valuetext={`Current slider's value is ${value}.`}>
            {false && <div className={styles.slider_handle_triangle} style={{borderTopColor: settings.handleColor}}/>}
          </div>
        </div>
      </div>
    </div>
  }
}

Slider.propTypes = {
  onChange: PropTypes.func.isRequired,
  model: PropTypes.number.isRequired
};

export default Slider;